import logger from '../../Utils/logger';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Service } from '../../Components/Service/Service';
import { ServiceCategoryNav } from '../../Components/ServiceCategoryNav/ServiceCategoryNav';
import { useGetServiceCategories } from '../../APICalling/useApiQueries';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import Loader from '../../Components/Service/Loader';
import { Header } from '../../Components/Header/Header';
import { useSticky } from '../../Utils/Helper';
import styles from './Style.module.css';
import Placeholder from '../../Components/Placeholder/Placeholder';
import CloseCircleIcon from '../../Components/Icons/CloseCircleIcon';
import AvatarCircleIcon from '../../Components/Icons/AvatarCircleIcon';
import { getPrimaryGuestName, guestHeaderLabel } from '../../Utils/guestMembers';
import Cart from '../../Components/Cart/Cart';
import { Check, Pencil, Trash2 } from 'lucide-react';
import { useAppointment } from '../../Context/Appointment/AppointmentContext';
import { useLocation } from '../../Context/Location/LocationContext';
import { useUI } from '../../Context/UI/UIContext';
import { useGuests } from '../../Context/Guests/GuestsContext';
import { useSchedule } from '../../Context/Schedule/ScheduleContext';
import { removeSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';
import { useValidateBookingSession } from '../../Utils/Hooks/useValidateBookingSession';
import { useWebSettings } from '../../Context/WebSettings/WebSettingsContext';

const SCROLL_SUPPRESS_MS = 700;
const SECTION_SCROLL_OFFSET = 8;

/** Sync category service counters with the active member's selections (group booking). */
function mergeServiceListWithMember(prevList, localMember) {
  const memberServices = localMember?.services ?? [];

  if (!Array.isArray(prevList) || prevList.length === 0) return prevList;

  if (memberServices.length === 0) {
    return prevList.map((item) => ({
      ...item,
      services: Array.isArray(item.services)
        ? item.services.map((subItem) => ({
            ...subItem,
            count: 0,
            isCounterVisible: false,
          }))
        : [],
    }));
  }

  return prevList.map((item) => ({
    ...item,
    services: Array.isArray(item.services)
      ? item.services.map((subItem) => {
          const slotsForId = memberServices.filter((s) => s.id === subItem.id);
          const slotCount = slotsForId.length;
          return {
            ...subItem,
            count: slotCount,
            isCounterVisible: slotCount > 0,
          };
        })
      : [],
  }));
}

export const SelectServices = () => {
  useValidateBookingSession();
  const { appointmentTypeID } = useAppointment();
  const { outletTypeID, selectedOutletID } = useLocation();
  const { isCartActive } = useUI();
  const { members, activeGuestId, removeGuest, updateMember, updateMembers, serviceCount: guestServiceCount, activeGuestServiceCount, selectCartTotal, selectActiveGuestSubtotal, selectedServices, primaryCustomer } = useGuests();
  const { setSelectedDate, setSelectedTime } = useSchedule();
  const { maxServiceCount } = useWebSettings();

  const isSingleBooking = Number(appointmentTypeID) === 1;
  const isGroupBooking = Number(appointmentTypeID) === 2;
  const memberServiceFlow = isSingleBooking || isGroupBooking;

  const [serviceList, setServiceList] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [stickyBlockHeight, setStickyBlockHeight] = useState(0);
  const [isEditingGuestName, setIsEditingGuestName] = useState(false);
  const [isHeaderFocused, setIsHeaderFocused] = useState(false);
  const [guestNameInput, setGuestNameInput] = useState('');
  const navigate = useNavigate();
  const stickyHeaderRef = useRef(null);
  const { isSticky, sentinelRef } = useSticky({ headerRef: stickyHeaderRef });
  const { membership } = useAppointment();

  // Compute derived values
  const cartTotal = useMemo(() => selectCartTotal(membership), [selectCartTotal, membership]);
  const activeGuestSubtotal = useMemo(() => selectActiveGuestSubtotal(membership), [selectActiveGuestSubtotal, membership]);
  const serviceCount = guestServiceCount;
  const sectionRefs = useRef(new Map());
  const categoryRefs = useRef(new Map());
  const suppressObserverRef = useRef(false);
  const previousOutletIDRef = useRef(selectedOutletID);
  const suppressTimerRef = useRef(null);
  const guestNameInputRef = useRef(null);

  const updateMemberList = useCallback((_, serviceData, isRemoving = false) => {
    updateMembers((prevMembers) =>
      prevMembers.map((member) => {
        if (member.id !== activeGuestId) return member;
        if (!isRemoving) {
          if ((member.services?.length || 0) >= maxServiceCount) return member;
          return {
            ...member,
            services: [...(member.services || []), serviceData],
          };
        }
        const services = member.services || [];
        const index = services.findIndex((svc) => svc.id === serviceData.id);
        if (index === -1) return member;
        let updatedServices = services.filter((_, i) => i !== index);
        // The 15-minute service can't stay selected on its own — it needs another service alongside it.
        const hasNonFifteenMinService = updatedServices.some((svc) => svc.duration && Number(svc.duration) > 15);
        if (!hasNonFifteenMinService) {
          updatedServices = updatedServices.filter((svc) => !(svc.duration && Number(svc.duration) === 15));
        }
        return {
          ...member,
          services: updatedServices,
        };
      })
    );
  }, [activeGuestId, maxServiceCount, updateMembers]);

  const queryParams = useMemo(() => ({
    pagination: 0,
    status: 1,
    outlet_type: outletTypeID,
    outlet: selectedOutletID,
    is_image: 1,
  }), [outletTypeID, selectedOutletID]);

  const { data: queryData, isLoading, isError } = useGetServiceCategories(queryParams);
  const serviceCategoryData = useMemo(() => {
    const categories = queryData?.data?.data?.list?.category;
    return Array.isArray(categories) ? categories : [];
  }, [queryData]);
  const hasServices = serviceCategoryData.some(
    (category) => Array.isArray(category?.services) && category.services.length > 0
  );

  const categoryIdsKey = useMemo(
    () => (serviceList?.length ? serviceList.map((c) => c.id).join(",") : ""),
    [serviceList]
  );

  const serviceListRef = useRef(serviceList);
  serviceListRef.current = serviceList;

  const setSectionRef = useCallback((categoryId) => (el) => {
    const map = sectionRefs.current;
    if (el) map.set(categoryId, el);
    else map.delete(categoryId);
  }, []);

  const setCategoryRef = useCallback((categoryId) => (el) => {
    const map = categoryRefs.current;
    if (el) map.set(categoryId, el);
    else map.delete(categoryId);
  }, []);

  useLayoutEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;

    const measure = () => {
      setStickyBlockHeight(el.offsetHeight || 0);
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => measure());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isSticky, isLoading, categoryIdsKey]);

  useEffect(() => {
    if (!serviceCategoryData || isLoading) return;

    const storedServiceList = JSON.parse(sessionStorage.getItem("service_list")) || [];
    let apiData = serviceCategoryData.map((item) => ({
      ...item,
      services: Array.isArray(item?.services)
        ? item.services.map((subItem) => {
            if (subItem.is_service_available === 0 && subItem.reason) {
              logger.log(`Service unavailable — "${subItem.name}" (id: ${subItem.id}): ${subItem.reason}`);
            }
            return { ...subItem, count: 0, isCounterVisible: false };
          })
        : [],
    }));

    if (storedServiceList.length > 0) {
      apiData = apiData.map((apiItem) => {
        const storedItem = storedServiceList.find((sItem) => sItem.id === apiItem.id);
        return storedItem ? { ...apiItem, ...storedItem } : apiItem;
      });
    }

    setServiceList([...apiData]);
  }, [serviceCategoryData, isLoading, setServiceList]);

  const activeGuest = useMemo(
    () => members.find((m) => m.id === activeGuestId) ?? members[0],
    [members, activeGuestId]
  );

  const editableGuestName = useMemo(() => {
    if (!activeGuest) return "";
    const customerName = (activeGuest.customerName || "").trim();
    if (customerName) return customerName;
    if (activeGuest.isPrimary) return getPrimaryGuestName();
    return activeGuest.name || activeGuest.displayName || "";
  }, [activeGuest]);

  const activeGuestServicesKey = useMemo(
    () => JSON.stringify(activeGuest?.services ?? []),
    [activeGuest]
  );

  useEffect(() => {
    setGuestNameInput(editableGuestName);
    setIsEditingGuestName(false);
    setIsHeaderFocused(false);
  }, [editableGuestName, activeGuestId]);

  useEffect(() => {
    if (!isEditingGuestName) return;
    guestNameInputRef.current?.focus();
    guestNameInputRef.current?.select();
  }, [isEditingGuestName]);

  useEffect(() => {
    if (isLoading) return;
    setServiceList((prevList) => {
      if (!prevList?.length) return prevList;
      const localMember = members.find((m) => m.id === activeGuestId);
      return mergeServiceListWithMember(prevList, localMember);
    });
  }, [isLoading, activeGuestId, activeGuestServicesKey, setServiceList, members]);

  useEffect(() => {
    if (isLoading) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isLoading]);

  // Clear date and time state when outlet ID changes
  useEffect(() => {
    if (previousOutletIDRef.current !== null && previousOutletIDRef.current !== selectedOutletID) {
      removeSessionStorageValue(STORAGE_KEYS.DATE);
      removeSessionStorageValue(STORAGE_KEYS.DATE_SELECTION_SOURCE);
      removeSessionStorageValue(STORAGE_KEYS.TIME);
      setSelectedDate('');
      setSelectedTime('');
    }
    previousOutletIDRef.current = selectedOutletID;
  }, [selectedOutletID, setSelectedDate, setSelectedTime]);

  // Clear date and time when leaving SelectServices page
  useEffect(() => {
    return () => {
      removeSessionStorageValue(STORAGE_KEYS.DATE);
      removeSessionStorageValue(STORAGE_KEYS.DATE_SELECTION_SOURCE);
      removeSessionStorageValue(STORAGE_KEYS.TIME);
      setSelectedDate('');
      setSelectedTime('');
    };
  }, [setSelectedDate, setSelectedTime]);

  const beginSuppressObserver = useCallback(() => {
    suppressObserverRef.current = true;
    if (suppressTimerRef.current) {
      clearTimeout(suppressTimerRef.current);
    }
    suppressTimerRef.current = setTimeout(() => {
      suppressObserverRef.current = false;
      suppressTimerRef.current = null;
    }, SCROLL_SUPPRESS_MS);
  }, []);

  useEffect(
    () => () => {
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    },
    []
  );

  const computeActiveSection = useCallback(() => {
    const list = serviceListRef.current;
    if (suppressObserverRef.current || !list?.length) return;

    const activationY =
      stickyHeaderRef.current?.getBoundingClientRect?.().bottom ?? stickyBlockHeight;

    let bestId = list[0].id;
    let bestTop = Number.NEGATIVE_INFINITY;

    for (const cat of list) {
      const el = sectionRefs.current.get(cat.id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      if (top <= activationY + 12 && top > bestTop) {
        bestTop = top;
        bestId = cat.id;
      }
    }

    // A short last section can never scroll up to the activation line (the
    // page runs out of scroll first), so it would never become active. Once
    // the window is scrolled to the bottom, the last section the user can
    // reach is the one they are looking at.
    const doc = document.documentElement;
    const maxScroll = doc.scrollHeight - window.innerHeight;
    if (maxScroll > 0 && Math.ceil(window.scrollY) >= maxScroll - 2) {
      for (let i = list.length - 1; i >= 0; i--) {
        if (sectionRefs.current.get(list[i].id)) {
          bestId = list[i].id;
          break;
        }
      }
    }

    setActiveCategory((prev) => (prev !== bestId ? bestId : prev));
  }, [stickyBlockHeight]);

  useEffect(() => {
    if (isLoading || !categoryIdsKey) return;

    let rafId = null;
    const scheduleCompute = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        computeActiveSection();
      });
    };

    const observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(scheduleCompute, {
            root: null,
            rootMargin: "0px",
            threshold: [0, 0.05, 0.1, 0.25, 0.5, 0.75, 1],
          })
        : null;

    serviceListRef.current.forEach((cat) => {
      const el = sectionRefs.current.get(cat.id);
      if (el && observer) observer.observe(el);
    });

    window.addEventListener("scroll", scheduleCompute, { passive: true });
    window.addEventListener("resize", scheduleCompute);
    scheduleCompute();

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", scheduleCompute);
      window.removeEventListener("resize", scheduleCompute);
      observer?.disconnect();
    };
  }, [isLoading, categoryIdsKey, computeActiveSection]);

  useEffect(() => {
    if (activeCategory == null) return;
    const btn = categoryRefs.current.get(activeCategory);
    if (!btn) return;
    const container = btn.parentElement;
    if (!container) return;
    // Center the active chip horizontally inside its own scroller only.
    // scrollIntoView({ block: "nearest" }) could nudge the window vertically and
    // cause micro-jumps while slow-scrolling, so scroll the container directly.
    const cRect = container.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    const containerStyle = window.getComputedStyle(container);
    const startInset = parseFloat(
      btn === container.firstElementChild
        ? containerStyle.paddingLeft
        : containerStyle.scrollPaddingLeft
    ) || 0;
    const delta = bRect.left - cRect.left - startInset;
    container.scrollBy({ left: delta, behavior: "smooth" });
  }, [activeCategory]);

  const handleCategoryClick = useCallback(
    (categoryId) => {
      const el = sectionRefs.current.get(categoryId);
      if (!el) return;

      setActiveCategory(categoryId);
      beginSuppressObserver();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [beginSuppressObserver]
  );


// REDIRECTION TO HOME WHEN VALUE IS NOT AVAILABLE
useEffect(() => {
  if (appointmentTypeID == null) {
    navigate('/');
  }  
}, [
  appointmentTypeID,
  navigate,
]);



  const guestChipLabel = guestHeaderLabel(activeGuest);
  const showCartPadding = memberServiceFlow
    ? activeGuestServiceCount > 0
    : isCartActive;
  const cartDisplayTotal = memberServiceFlow ? activeGuestSubtotal : cartTotal ?? 0;
  const cartDisplayCount = memberServiceFlow ? activeGuestServiceCount : serviceCount ?? 0;

  const handleStartGuestRename = useCallback(() => {
    setGuestNameInput(editableGuestName);
    setIsEditingGuestName(true);
    setIsHeaderFocused(true);
  }, [editableGuestName]);

  const handleSaveGuestRename = useCallback(() => {
    const nextName = guestNameInput.trim();
    if (!activeGuest?.id) {
      setIsEditingGuestName(false);
      setIsHeaderFocused(false);
      return;
    }
    updateMember(activeGuest.id, { customerName: nextName });
    setIsEditingGuestName(false);
    setIsHeaderFocused(false);
  }, [activeGuest, guestNameInput, updateMember]);

  const handleRemoveActiveGuest = useCallback(() => {
    if (!isGroupBooking || !activeGuest?.id || activeGuest.isPrimary) return;
    removeGuest(activeGuest.id);
    navigate('/guests-services', { replace: true });
  }, [activeGuest, isGroupBooking, navigate, removeGuest]);

  const noServiceSelected = useMemo(() => {
    return Number(serviceCount ?? 0) === 0;
  }, [serviceCount]);


  return (
    <>
    <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
    <div ref={stickyHeaderRef} className={`sticky-header ${isSticky ? "is-sticky" : ""} pb-0`}>
        <Header 
        title="Select Services"
        noServiceSelected={noServiceSelected}
        
        />
        <div className={`${styles.selectServicesHeader} select-services-header space-x space-y container-md`}>
          <div className={`${styles.headerContent} ${isHeaderFocused ? styles.focused : ""} ${isGroupBooking && !activeGuest?.isPrimary ? styles.hasGuestActions : ""}`}>
            <div className={styles.leftContent}>
              <div className={styles.avatarCircleIcon}>
                <AvatarCircleIcon width={28} height={28} />
              </div>
              {isEditingGuestName ? (
                <input
                  ref={guestNameInputRef}
                  type="text"
                  value={guestNameInput}
                  onChange={(e) => setGuestNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveGuestRename();
                    if (e.key === "Escape") {
                      setGuestNameInput(editableGuestName);
                      setIsEditingGuestName(false);
                      setIsHeaderFocused(false);
                    }
                  }}
                  className={styles.guestNameInput}
                  placeholder="Type guest name"
                  aria-label="Guest name"
                  maxLength={30}
                />
              ) : (
                <div className={styles.guestNameLabel}>{guestChipLabel}</div>
              )}
            </div>
            {isGroupBooking && !activeGuest?.isPrimary && (
            <div className={styles.rightContent}>
              {isEditingGuestName ? (
                  <button
                    type="button"
                    className={`${styles.saveGuestNameBtn} btn`}
                    aria-label="Save Guest Name"
                    onClick={handleSaveGuestRename}
                    title="Save Guest Name"
                  >
                    <Check size={16} color="var(--PrimaryColor800)" />
                  </button>
                ) : (
                  <>
                  <button
                    type="button"
                    className={`${styles.renameGuestNameBtn} btn`}
                    aria-label="Rename Guest Name"
                    onClick={handleStartGuestRename}
                    title="Rename Guest Name"
                  >
                    <Pencil size={16} color="var(--PrimaryColor800)" />
                  </button>
                  <button
                    type="button"
                    className={`${styles.removeGuestBtn} btn`}
                    aria-label={`Remove ${guestChipLabel || "Guest"}`}
                    onClick={handleRemoveActiveGuest}
                    title={`Remove ${guestChipLabel || "Guest"}`}
                  >
                    <Trash2 size={16} color="var(--DangerColor)" />
                  </button>
                  </>
                )}
            </div>
            )}
          </div>
        </div>
        {hasServices && serviceList.length > 0 &&
          <ServiceCategoryNav
            categories={serviceList}
            activeCategory={activeCategory}
            setCategoryRef={setCategoryRef}
            onCategoryClick={handleCategoryClick}
          />
        }
    </div>
      
    <div
      className={`${styles.serviceContent} space-x space-y container-md ${
        showCartPadding ? "cart-space-b" : ""
      }`}
    >
      {isLoading ? 
      <>
      <Loader/>
      </>
      : isError && !queryData ? (
        <Placeholder
          heading="Unable to load services"
          para="Please try again later"
          icon={<CloseCircleIcon />}
        />
      ) :
      <div>
        {hasServices && serviceList.length > 0 ?
          serviceList.map((serv) => (
            <div
              key={serv.id}
              className={styles.serviceSection}
              ref={setSectionRef(serv.id)}
              style={{
                scrollMarginTop: stickyBlockHeight + SECTION_SCROLL_OFFSET,
              }}
            >
              <Service
                id={serv?.id}
                title={serv?.name || <Skeleton/>}
                description={serv?.description || ""}
                image={serv?.image}
                services={serv?.services || <Skeleton />}
                memberServiceFlow={memberServiceFlow}
                updateMemberList={updateMemberList}
                members={members}
                selectedMember={members.findIndex((m) => m.id === activeGuestId)}
              />
            </div>
          )) : 
          <>
          <Placeholder
            heading="No service is available"
            para="Please select another outlet or try again later"
            icon={<CloseCircleIcon />}
          />
          </>
          }
      </div>
      }
    </div>

    <Cart
    btnLabel="Add"
    btnClick={() => navigate("/guests-services")}
    isCartActive={showCartPadding}
    disabled={false}
    displayTotal={cartDisplayTotal}
    displayCount={cartDisplayCount}
    selectedServices={selectedServices}
    />

    </>
  );
};
